import {
  Checkbox,
  CheckboxProps,
  Combobox,
  createDatePickerValidator,
  createForm,
  DatePicker,
  Divider,
  Flex,
  Heading,
  Notice,
  Select,
  Text,
  TextInput,
  TextInputProps,
  useToasts,
} from '@applyboard/crystal-ui'
import {
  COUNTRY_NAMES,
  CountryIsoCode,
} from '@applyboard/school-backend-lib/dist/common-types/country'
import { LANGUAGE_NAMES } from '@applyboard/school-backend-lib/dist/common-types/language'
import { GENDER_NAMES, GenderCode } from '@applyboard/school-backend-lib/dist/common-types/gender'
import { RawApplicationResponse, useDeletePassport, useUpdateApplication } from '../../../../hooks'
import { GenericError } from '../../../../utils'
import { StudentApplication, UploadFileData } from '../../types'
import { useApplicationFormContext } from '../ApplicationForm'
import { useGetDestinationCountry } from '../../../../hooks/useGetDestinationCountry'
import { useEffect, useState, ComponentType } from 'react'
import { getFilesOfType } from '../utils'
import { DestinationCountryStatus, DocumentTags } from '../../../../utils/enums'
import { DocumentType, FileData } from '@applyboard/school-applications-types-lib'
import { FileUploadField } from '../FileUploadField'
import { useUploadPassport } from '../../../../hooks/useUploadPassport'
import { transformFileInfoToUploadFileData } from '../../../../utils/transformFileInfoToUploadFileData'
import {
  isPassportProcessingComplete,
  Passport,
  useGetPassport,
} from '../../../../hooks/useGetPassport'
import { Loading } from '../../../Loading'
import { LoadingField } from '../../../LoadingField'
import { RemovePassportDialog } from '../../../../pages/CreateApplicationPage/RemovePassportDialog'
import { useUpdatePassport, UpdatePassportParams } from '../../../../hooks/useUpdatePassport'
import { Asterisk } from '../../../Asterisk'
import { Warnings } from './Warnings'
import {
  copyResidentialAddress,
  hasUserModifiedExtractedPassportDetails,
  getExtractionHelperText,
  getDefaultValue,
} from './utils'
import { PersonalInformationFormFields } from './types'
import { StatusSection } from './StatusSection'
import { ALLOWED_PASSPORT_FILE_TYPES, UPLOAD_LIMIT } from './consts'
import { getPassportFile } from '../../../../hooks/useGetPassportFile'
import { SpecificField } from '../../SpecificField/SpecificField'
import { Student, useGetStudent } from '../../../../hooks/useGetStudent'
import { useUpdateStudent } from '../../../../hooks/useUpdateStudent'
import { useQueryClient } from '@tanstack/react-query'
import { useGetApplicationPageProps } from '../../../../hooks/useGetApplicationPageProps'
import {
  specificFieldValidations,
  getDocumentProcessingConfigPassportEnabled,
} from '../../../../utils/specificFieldValidations'
import { useGetLatestPassportFromIds } from '../../../../hooks/useGetLatestPassportFromIds'

const { Form, Field, useFieldValues, useSetFieldValues } =
  createForm<PersonalInformationFormFields>()

type PersonalInformationTabProps = {
  disabled?: boolean
  application: StudentApplication
  onSubmit?: () => void
  onSuccess: (response?: RawApplicationResponse) => void
  onError: (err: GenericError) => void
  updateApplication: ReturnType<typeof useUpdateApplication>['updateApplication']
  formId: string
}

export function PersonalInformationTab(props: Readonly<PersonalInformationTabProps>) {
  const queryClient = useQueryClient()
  const { student, isLoadingStudent } = useGetStudent()
  const { updateStudent } = useUpdateStudent()
  const passports = Object.values(
    props.application.attributes?.statusAndCitizenship?.passports || {},
  )
  const [passportId, setPassportId] = useState<string | undefined>()
  const [passportIds, setPassportIds] = useState<string[]>(() =>
    passports.map(p => p.passportId || ''),
  )

  const { applicationPageProps } = useGetApplicationPageProps()
  const passportProcessingTimeout =
    applicationPageProps?.documentProcessingConfig?.documentTypes?.PASSPORT?.timeout

  const [hasAiProcessingTimedOut, setHasAiProcessingTimedOut] = useState(false)

  const {
    isLoadingPassport: isLoadingPassports,
    isFetchingPassport: isFetchingPassports,
    passport: latestPassport,
  } = useGetLatestPassportFromIds({
    ids: passportIds,
    setHasAiProcessingTimedOut,
    configTimeout: passportProcessingTimeout,
    enabled: !passportId && passportIds.length > 0,
  })

  const { isLoadingPassport, isFetchingPassport, passport } = useGetPassport({
    id: passportId,
    setHasAiProcessingTimedOut,
    configTimeout: passportProcessingTimeout,
  })

  const { isUpdatingPassport, updatePassport } = useUpdatePassport()

  const { resetFiles, pendingFileUploadState } = useApplicationFormContext()
  const { destinationCountryName } = useGetDestinationCountry()

  const isAiProcessingEnabled = getDocumentProcessingConfigPassportEnabled(
    applicationPageProps?.documentProcessingConfig,
  )

  useEffect(() => {
    resetFiles(
      getFilesOfType(
        [DocumentTags.PASSPORT, DocumentTags.DESTINATION_COUNTRY_STATUS],
        props.application?.attributes?.files as FileData,
      ),
    )
  }, [props.application?.attributes?.files, resetFiles])

  useEffect(() => {
    setHasAiProcessingTimedOut(false)
    if (passportId && !passportIds.includes(passportId)) {
      setPassportIds([...passportIds, passportId])
    }
  }, [passportId, passportIds])

  useEffect(() => {
    if (latestPassport) {
      setPassportId(latestPassport.id)
    }
  }, [isLoadingPassports, isFetchingPassports, latestPassport])

  if (isLoadingStudent || (!isAiProcessingEnabled && passportId && isLoadingPassport)) {
    return <Loading />
  }

  function createUpdateApplicationPayload(
    data: PersonalInformationFormFields,
    passportId: string | undefined,
    pendingFileUploadState: Record<string, UploadFileData | null>,
  ) {
    return {
      attributes: {
        personalInformation: {
          basicPersonalInformation: {
            givenName: data.givenName,
            middleName: data.middleName,
            familyName: data.familyName,
            dateOfBirth: data.dateOfBirth?.substring(0, 10),
            nationality: (data.nationality as CountryIsoCode) ?? null,
            gender: (data.gender as GenderCode) ?? null,
          },
          addresses: {
            residentialAddress: {
              country: data.residentialAddressCountry as CountryIsoCode,
              addressLine1: data.residentialAddressLine1,
              addressLine2: data.residentialAddressLine2,
              city: data.residentialAddressCity,
              province: data.residentialAddressProvince,
              postalCode: data.residentialAddressPostalCode,
            },
            mailingAddress: {
              country: data.mailingAddressCountry as CountryIsoCode,
              addressLine1: data.mailingAddressLine1,
              addressLine2: data.mailingAddressLine2,
              city: data.mailingAddressCity,
              province: data.mailingAddressProvince,
              postalCode: data.mailingAddressPostalCode,
            },
          },
        },
        statusAndCitizenship: {
          destinationCountryStatus: data.immigrationStatus as DestinationCountryStatus,
          passports: passportId
            ? {
                [passportId]: {
                  passportId,
                },
              }
            : undefined,
        },
      },
      files: pendingFileUploadState,
    }
  }

  const handleSubmit = async (data: PersonalInformationFormFields) => {
    if (props.disabled) {
      props.onSuccess()
      return
    }

    const payload = createUpdateApplicationPayload(data, passportId, pendingFileUploadState)

    try {
      const updateApplicationPromise = new Promise<RawApplicationResponse>((resolve, reject) => {
        props.updateApplication(payload, {
          onSuccess: response => {
            resetFiles(
              getFilesOfType(
                [DocumentTags.PASSPORT, DocumentTags.DESTINATION_COUNTRY_STATUS],
                response.data?.attributes?.files as FileData,
              ),
            )
            resolve(response)
          },
          onError: error => reject(error instanceof Error ? error : new Error(String(error))),
        })
      })

      // Only perform the passport update if necessary
      const passportModification = hasUserModifiedExtractedPassportDetails(data, passport)
      const updatePassportPromise = passportModification.passportModified
        ? new Promise((resolve, reject) => {
            updatePassport(
              {
                id: passportId || '',
                passportNumber: passportModification.passportNumberModified
                  ? data.passportNumber
                  : undefined,
                expiryDate: passportModification.passportExpiryModified
                  ? data.passportExpiryDate
                  : undefined,
                nationality: passportModification.passportNationalityModified
                  ? data.nationality
                  : undefined,
              } as UpdatePassportParams,
              {
                onSuccess: async response => {
                  await queryClient.invalidateQueries({
                    queryKey: ['passport', response.data.id],
                  })
                  resolve(response)
                },
                onError: error => reject(error instanceof Error ? error : new Error(String(error))),
              },
            )
          })
        : Promise.resolve(null)

      const updateStudentPromise = student?.id
        ? new Promise((resolve, reject) => {
            updateStudent(
              {
                id: student.id,
                attributes: {
                  personalInformation: {
                    previousName: data.previousName,
                    preferredName: data.preferredName,
                    firstLanguageSpoken:
                      data.firstLanguageSpoken !== '' ? data.firstLanguageSpoken : null,
                  },
                },
              },
              {
                onSuccess: response => {
                  queryClient.setQueryData(['students'], () => response)
                  resolve(response)
                },
                onError: error => reject(error instanceof Error ? error : new Error(String(error))),
              },
            )
          })
        : Promise.resolve(null)

      // Wait for both operations to succeed. If any fails, the catch block is executed.
      const [appResponse] = await Promise.all([
        updateApplicationPromise,
        updatePassportPromise,
        updateStudentPromise,
      ])

      props.onSuccess(appResponse)
    } catch (error) {
      props.onError(error as GenericError)
      return
    }
  }

  return (
    <Form
      id={props.formId}
      defaultValues={getDefaultValue({
        passport,
        application: props.application,
        student,
      })}
      onSubmit={handleSubmit}
    >
      <PersonalInformationTabFields
        disabled={props.disabled}
        isRequestingPassportApi={isUpdatingPassport ?? isFetchingPassport}
        application={props.application}
        destinationCountryName={destinationCountryName}
        passport={passport}
        setPassportId={setPassportId}
        student={student}
        isAiProcessingEnabled={isAiProcessingEnabled}
        isLoadingPassport={isLoadingPassport}
        hasAiProcessingTimedOut={hasAiProcessingTimedOut}
      />
    </Form>
  )
}

type PersonalInformationFormFieldsProps = {
  disabled?: boolean
  application: StudentApplication
  student?: Student
  destinationCountryName: string
  passport?: Passport
  setPassportId: (id: string) => void
  isRequestingPassportApi?: boolean
  isAiProcessingEnabled: boolean
  isLoadingPassport?: boolean
  hasAiProcessingTimedOut?: boolean
}

function PersonalInformationTabFields(props: Readonly<PersonalInformationFormFieldsProps>) {
  const queryClient = useQueryClient()
  const [showUploadNotice, setShowUploadNotice] = useState(true)
  const toast = useToasts()
  const [showUploadErrorNotice, setShowUploadErrorNotice] = useState(false)

  const { applicationPageProps } = useGetApplicationPageProps()
  const { isSpecificFieldRequired } = specificFieldValidations(applicationPageProps?.specificFields)

  const isAiProcessingEnabled = props.isAiProcessingEnabled

  const { isUploadingPassport, uploadPassport } = useUploadPassport()
  const {
    immigrationFiles,
    immigrationStatus,
    isMailingAddressResidentialAddress,
    residentialAddressCountry,
    residentialAddressLine1,
    residentialAddressLine2,
    residentialAddressCity,
    residentialAddressProvince,
    residentialAddressPostalCode,
    givenName,
    familyName,
    passportNumber,
    passportExpiryDate,
    gender,
    dateOfBirth,
    nationality,
  } = useFieldValues([
    'immigrationFiles',
    'immigrationStatus',
    'isMailingAddressResidentialAddress',
    'residentialAddressCountry',
    'residentialAddressLine1',
    'residentialAddressLine2',
    'residentialAddressCity',
    'residentialAddressProvince',
    'residentialAddressPostalCode',
    'givenName',
    'familyName',
    'passportNumber',
    'passportExpiryDate',
    'gender',
    'dateOfBirth',
    'nationality',
  ])
  const setFieldValues = useSetFieldValues()
  const maxDate = new Date()
  const minDate = new Date(1900, 0, 1)
  maxDate.setFullYear(maxDate.getFullYear() + 10)
  minDate.setFullYear(minDate.getFullYear() - 3)

  const [openRemovePassportDialog, setOpenRemovePassportDialog] = useState(false)
  const { deletePassport, isDeletingPassport } = useDeletePassport()

  const datePickerValidator = createDatePickerValidator(minDate, maxDate, 'day')

  const isExtractingData = !isPassportProcessingComplete(
    props.passport,
    props.hasAiProcessingTimedOut,
  )

  useEffect(() => {
    if (!isExtractingData && isAiProcessingEnabled) {
      const defaultValues = getDefaultValue({
        application: props.application,
        passport: props.passport,
        student: props.student,
      })

      let address = {}

      if (isMailingAddressResidentialAddress) {
        address = copyResidentialAddress({
          residentialAddressCountry: defaultValues.residentialAddressCountry,
          residentialAddressLine1: defaultValues.residentialAddressLine1,
          residentialAddressLine2: defaultValues.residentialAddressLine2,
          residentialAddressCity: defaultValues.residentialAddressCity,
          residentialAddressProvince: defaultValues.residentialAddressProvince,
          residentialAddressPostalCode: defaultValues.residentialAddressPostalCode,
        })
      }

      setFieldValues({
        ...defaultValues,
        ...address,
      })
    }
  }, [
    props.passport,
    isExtractingData,
    isAiProcessingEnabled,
    isMailingAddressResidentialAddress,
    props.application,
    props.student,
    setFieldValues,
  ])

  // listen to changes in hasAiProcessingTimedOut
  useEffect(() => {
    // Add any necessary side effects here if needed in the future
  }, [props.hasAiProcessingTimedOut])

  // Reset upload notice visibility when a new passport is uploaded
  useEffect(() => {
    if (props.passport?.id) {
      setShowUploadNotice(true)
    }
  }, [props.passport?.id])

  const renderPassportUploadSection = () => {
    if (!isAiProcessingEnabled) {
      return null
    }

    return (
      <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap pt={4} pb={4}>
        {(props.passport && isExtractingData) || isUploadingPassport ? (
          <Flex gap={4} direction="column" basis="100%">
            <Notice intent="primary">
              Processing your document... This might take a few minutes.
            </Notice>
          </Flex>
        ) : null}
        {!props.disabled &&
        props.passport &&
        props.passport.id !==
          Object.values(props.application.attributes?.statusAndCitizenship?.passports ?? {})[0]
            ?.passportId &&
        showUploadNotice &&
        !isExtractingData ? (
          <Flex gap={4} direction="column" basis="100%">
            <Warnings
              documentProcessStatus={props.passport.attributes.documentProcessStatus}
              documentTypeConfidence={
                props.passport.attributes.processingResults?.documentTypeConfidence
              }
              hasAiProcessingTimedOut={props.hasAiProcessingTimedOut}
              onClick={() => setShowUploadNotice(false)}
            />
          </Flex>
        ) : null}

        {showUploadErrorNotice ? (
          <Flex gap={4} direction="column" basis="100%">
            <Notice intent="negative">
              Upload failed. Please try again. If the problem persists, check your file format and
              size.
              <Notice.CloseButton onClick={() => setShowUploadErrorNotice(false)} />
            </Notice>
          </Flex>
        ) : null}
        <Field
          as={FileUploadField}
          allowedFileTypes={ALLOWED_PASSPORT_FILE_TYPES}
          application={props.application}
          disabled={props.disabled || isUploadingPassport}
          fileLimit={UPLOAD_LIMIT}
          fileType={DocumentTags.PASSPORT}
          label={
            props.passport || isUploadingPassport ? (
              ''
            ) : (
              <Text variant={'bodyM'}>
                Your passport is required to proceed with the application. We'll use it to:
                <ul>
                  <li>Verify your identity</li>
                  <li>Pre-fill application fields (e.g., name, date of birth, gender)</li>
                </ul>
                Add passport document below, supported file formats: JPG, JPEG, PDF, PNG, max number
                of files: {UPLOAD_LIMIT} <Asterisk />
              </Text>
            )
          }
          name={`passportFiles`}
          onRemove={() => {
            setOpenRemovePassportDialog(true)
          }}
          section={`${props.passport?.id}`}
          showHistory={!!props.disabled}
          validate={() => {
            if (!props.passport?.id) {
              return 'This field is required'
            }

            return true
          }}
          hideUploadComponent={Boolean(
            props.passport || isUploadingPassport || props.isLoadingPassport,
          )}
          showLoading={isUploadingPassport || props.isLoadingPassport}
          multiple={false}
          presetValue={
            props.passport
              ? [
                  {
                    id: props.passport.id,
                    uploadDate: new Date(props.passport.attributes.uploadedAt),
                    tags: [DocumentTags.PASSPORT],
                    name: props.passport.attributes.fileName,
                    section: '',
                    history: [],
                    isBlankDatePlaceholder: () => true,
                    onClick: async () => {
                      if (props.passport) {
                        const req = await getPassportFile(props.passport.id)
                        window.open(
                          req.data.meta.downloadUrl,
                          `passport_${props.passport.id}_${Date.now()}`,
                        )
                      }
                    },
                    onError: (error: Error) => {
                      toast.negative(error)
                    },
                  },
                ]
              : undefined
          }
          onChange={([passportFile]) => {
            if (passportFile) {
              setShowUploadErrorNotice(false)

              if (
                !ALLOWED_PASSPORT_FILE_TYPES.find(fileExtension =>
                  passportFile.file.name.toLowerCase().endsWith(fileExtension),
                )
              ) {
                passportFile.errorMessage = `Incorrect file format. Only the following file types may be uploaded: ${ALLOWED_PASSPORT_FILE_TYPES.join(
                  ', ',
                )}`

                return false
              }

              uploadPassport(
                transformFileInfoToUploadFileData(passportFile, {
                  sectionReference: `${props.passport?.id}`,
                  type: DocumentTags.PASSPORT as unknown as DocumentType,
                }),
                {
                  onSuccess: response => {
                    props.setPassportId(response.data.id)
                  },
                  onError: err => {
                    setShowUploadErrorNotice(true)
                    passportFile.errorMessage = `Error while trying to upload the file. Please try again.`
                    setFieldValues({ passportFiles: [passportFile] })
                    toast.negative(err)
                  },
                },
              )
            }
          }}
        />
      </Flex>
    )
  }

  const renderFormFields = () => {
    const shouldShowFields = isAiProcessingEnabled ? props.passport : true

    if (!shouldShowFields) {
      return null
    }

    return (
      <>
        <Heading variant="titleS" level={3}>
          ✈️ Step 1: Provide your passport
        </Heading>
        <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap pt={4} pb={4}>
          <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
            {isExtractingData && isAiProcessingEnabled ? (
              <LoadingField label="Passport number" required />
            ) : (
              <Field
                as={TextInput as ComponentType<TextInputProps>}
                label="Passport number"
                name="passportNumber"
                disabled={props.disabled || props.isRequestingPassportApi}
                required={!props.disabled ? 'Passport number is required' : false}
                {...(isAiProcessingEnabled
                  ? getExtractionHelperText({
                      applicationPassportId: Object.values(
                        props.application.attributes?.statusAndCitizenship?.passports ?? {},
                      )[0]?.passportId,
                      passportId: props.passport?.id,
                      extractedData:
                        props.passport?.attributes.processingResults?.extractedData?.passportNumber,
                      currentValue: passportNumber,
                    })
                  : {})}
              />
            )}
          </Flex.Item>
          <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
            {isExtractingData && isAiProcessingEnabled ? (
              <LoadingField label="Passport expiry date" required />
            ) : (
              <Field
                as={DatePicker}
                label="Passport expiry date"
                name="passportExpiryDate"
                maxDate={maxDate.toISOString()}
                minDate={minDate.toISOString()}
                disabled={props.disabled || props.isRequestingPassportApi}
                required={!props.disabled ? 'Passport expiry date is required' : false}
                {...(isAiProcessingEnabled
                  ? getExtractionHelperText({
                      applicationPassportId: Object.values(
                        props.application.attributes?.statusAndCitizenship?.passports ?? {},
                      )[0]?.passportId,
                      passportId: props.passport?.id,
                      extractedData:
                        props.passport?.attributes.processingResults?.extractedData?.expiryDate,
                      currentValue: passportExpiryDate,
                    })
                  : {})}
              />
            )}
          </Flex.Item>

          <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
            {isExtractingData && isAiProcessingEnabled ? (
              <LoadingField label="Given name" required />
            ) : (
              <Field
                as={TextInput as ComponentType<TextInputProps>}
                label="Given name"
                name="givenName"
                disabled={props.disabled || props.isRequestingPassportApi}
                required={!props.disabled ? 'Given name is required' : false}
                {...(isAiProcessingEnabled
                  ? getExtractionHelperText({
                      applicationPassportId: Object.values(
                        props.application.attributes?.statusAndCitizenship?.passports ?? {},
                      )[0]?.passportId,
                      passportId: props.passport?.id,
                      extractedData:
                        props.passport?.attributes.processingResults?.extractedData?.firstName,
                      currentValue: givenName,
                      field: 'firstName',
                    })
                  : {})}
              />
            )}
          </Flex.Item>
          <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
            {isExtractingData && isAiProcessingEnabled ? (
              <LoadingField label="Family name (if applicable)" />
            ) : (
              <Field
                as={TextInput as ComponentType<TextInputProps>}
                label="Family name (if applicable)"
                name="familyName"
                disabled={props.disabled || props.isRequestingPassportApi}
                {...(isAiProcessingEnabled
                  ? getExtractionHelperText({
                      applicationPassportId: Object.values(
                        props.application.attributes?.statusAndCitizenship?.passports ?? {},
                      )[0]?.passportId,
                      passportId: props.passport?.id,
                      extractedData:
                        props.passport?.attributes.processingResults?.extractedData?.familyName,
                      currentValue: familyName,
                      field: 'familyName',
                    })
                  : {})}
              />
            )}
          </Flex.Item>
          <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
            {isExtractingData && isAiProcessingEnabled ? (
              <LoadingField label="Middle name" />
            ) : (
              <Field
                as={TextInput as ComponentType<TextInputProps>}
                label="Middle name"
                name="middleName"
                disabled={props.disabled || props.isRequestingPassportApi}
              />
            )}
          </Flex.Item>
          <SpecificField
            field="personalInformation.previousName"
            forceEnabled={!!props.student?.attributes?.personalInformation?.previousName}
          >
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              {isExtractingData && isAiProcessingEnabled ? (
                <LoadingField label="Previous name" />
              ) : (
                <Field
                  as={TextInput as ComponentType<TextInputProps>}
                  label="Previous name"
                  name="previousName"
                  helpText="Full previous legal name, if applicable."
                  disabled={props.disabled || props.isRequestingPassportApi}
                  required={
                    !props.disabled && isSpecificFieldRequired('personalInformation.previousName')
                      ? 'Previous name is required'
                      : false
                  }
                />
              )}
            </Flex.Item>
          </SpecificField>
          <SpecificField
            field="personalInformation.preferredName"
            forceEnabled={!!props.student?.attributes?.personalInformation?.preferredName}
          >
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              {isExtractingData && isAiProcessingEnabled ? (
                <LoadingField label="Preferred name" />
              ) : (
                <Field
                  as={TextInput as ComponentType<TextInputProps>}
                  label="Preferred name"
                  name="preferredName"
                  disabled={props.disabled || props.isRequestingPassportApi}
                  required={
                    !props.disabled && isSpecificFieldRequired('personalInformation.preferredName')
                      ? 'Preferred name is required'
                      : false
                  }
                />
              )}
            </Flex.Item>
          </SpecificField>
          <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
            {isExtractingData && isAiProcessingEnabled ? (
              <LoadingField label="Gender" required />
            ) : (
              <Field
                as={Select}
                label="Gender"
                name="gender"
                appearance="styled"
                disabled={props.disabled || props.isRequestingPassportApi}
                required={!props.disabled ? 'Gender is required' : false}
                {...(isAiProcessingEnabled
                  ? getExtractionHelperText({
                      applicationPassportId: Object.values(
                        props.application.attributes?.statusAndCitizenship?.passports ?? {},
                      )[0]?.passportId,
                      passportId: props.passport?.id,
                      extractedData:
                        props.passport?.attributes.processingResults?.extractedData?.sex,
                      currentValue: gender,
                    })
                  : {})}
              >
                {Object.entries(GENDER_NAMES).map(([genderCode, genderName]) => (
                  <Select.Option key={genderCode} label={genderName} value={genderCode} />
                ))}
              </Field>
            )}
          </Flex.Item>
          <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
            {isExtractingData && isAiProcessingEnabled ? (
              <LoadingField label="Date of birth" required />
            ) : (
              <Field
                as={DatePicker}
                label="Date of birth"
                name="dateOfBirth"
                maxDate={maxDate.toISOString()}
                minDate={minDate.toISOString()}
                disabled={props.disabled || props.isRequestingPassportApi}
                required={!props.disabled ? 'Date of birth is required' : false}
                validate={datePickerValidator}
                {...(isAiProcessingEnabled
                  ? getExtractionHelperText({
                      applicationPassportId: Object.values(
                        props.application.attributes?.statusAndCitizenship?.passports ?? {},
                      )[0]?.passportId,
                      passportId: props.passport?.id,
                      extractedData:
                        props.passport?.attributes.processingResults?.extractedData?.birthDate,
                      currentValue: dateOfBirth,
                    })
                  : {})}
              />
            )}
          </Flex.Item>
          <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
            {isExtractingData && isAiProcessingEnabled ? (
              <LoadingField label="Nationality" required />
            ) : (
              <Field
                as={Combobox}
                label="Nationality"
                name="nationality"
                size="md"
                placeholder="Select"
                disabled={props.disabled || props.isRequestingPassportApi}
                required={!props.disabled ? 'Nationality is required' : false}
                {...(isAiProcessingEnabled
                  ? getExtractionHelperText({
                      applicationPassportId: Object.values(
                        props.application.attributes?.statusAndCitizenship?.passports ?? {},
                      )[0]?.passportId,
                      passportId: props.passport?.id,
                      extractedData:
                        props.passport?.attributes.processingResults?.extractedData?.nationality,
                      currentValue: nationality,
                    })
                  : {})}
              >
                {Object.entries(COUNTRY_NAMES).map(([countryCode, countryName]) => (
                  <Combobox.Option key={countryCode} label={countryName} value={countryCode} />
                ))}
              </Field>
            )}
          </Flex.Item>
          <SpecificField
            field="personalInformation.firstLanguageSpoken"
            forceEnabled={!!props.student?.attributes?.personalInformation?.firstLanguageSpoken}
          >
            <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
              {isExtractingData && isAiProcessingEnabled ? (
                <LoadingField label="First language spoken" />
              ) : (
                <Field
                  as={Combobox}
                  label="First language spoken"
                  name="firstLanguageSpoken"
                  size="md"
                  type="search"
                  placeholder="Select"
                  disabled={props.disabled || props.isRequestingPassportApi}
                  required={
                    !props.disabled &&
                    isSpecificFieldRequired('personalInformation.firstLanguageSpoken')
                      ? 'First language spoken is required'
                      : false
                  }
                >
                  {Object.entries(LANGUAGE_NAMES).map(([languageCode, languageName]) => (
                    <Combobox.Option key={languageCode} label={languageName} value={languageCode} />
                  ))}
                </Field>
              )}
            </Flex.Item>
          </SpecificField>
        </Flex>

        {/* Passport upload section when AI processing is disabled */}
        {!isAiProcessingEnabled ? (
          <>
            <Flex gap={4} direction="column" pt={4} pb={4}>
              {showUploadErrorNotice ? (
                <Flex gap={4} direction="column" basis="100%">
                  <Notice intent="negative">
                    Upload failed. Please try again. If the problem persists, check your file format
                    and size.
                    <Notice.CloseButton onClick={() => setShowUploadErrorNotice(false)} />
                  </Notice>
                </Flex>
              ) : null}
              <Flex direction="column" gap={4}>
                {(!props.passport?.id || isUploadingPassport) && (
                  <Heading level={2} variant="titleS">
                    Upload your passport
                  </Heading>
                )}
                <Field
                  as={FileUploadField}
                  allowedFileTypes={ALLOWED_PASSPORT_FILE_TYPES}
                  application={props.application}
                  disabled={props.disabled || isUploadingPassport}
                  fileLimit={UPLOAD_LIMIT}
                  fileType={DocumentTags.PASSPORT}
                  label={
                    props.passport || isUploadingPassport ? (
                      ''
                    ) : (
                      <Flex direction="column" gap={3}>
                        <Text variant="bodyM">
                          Please provide a clear and legible copy of your passport.
                        </Text>
                      </Flex>
                    )
                  }
                  name={`passportFiles`}
                  onRemove={() => {
                    deletePassport(
                      {
                        id: props.passport?.id,
                        applicationId: props.application.id,
                      },
                      {
                        onSuccess: () => {
                          queryClient.invalidateQueries({
                            queryKey: ['applications', props.application.id],
                          })
                          queryClient.removeQueries({
                            queryKey: ['passport', props.passport?.id],
                          })
                          setFieldValues({
                            passportFiles: [],
                          })
                          props.setPassportId('')
                          toast.positive('The passport has been successfully removed.')
                        },
                        onError: err => {
                          if (err instanceof Error) {
                            toast.negative(new Error(err.message))
                          }
                        },
                      },
                    )
                  }}
                  section={`${props.passport?.id}`}
                  showHistory={!!props.disabled}
                  validate={() => {
                    return true
                  }}
                  hideUploadComponent={Boolean(props.passport || isUploadingPassport)}
                  showLoading={isUploadingPassport || props.isLoadingPassport || isDeletingPassport}
                  multiple={false}
                  required={!props.disabled ? 'Passport File is required' : false}
                  presetValue={
                    props.passport
                      ? [
                          {
                            id: props.passport.id,
                            uploadDate: new Date(props.passport.attributes.uploadedAt),
                            tags: [DocumentTags.PASSPORT],
                            name: props.passport.attributes.fileName,
                            section: '',
                            history: [],
                            isBlankDatePlaceholder: () => true,
                            onClick: async () => {
                              if (props.passport) {
                                const req = await getPassportFile(props.passport.id)
                                window.open(
                                  req.data.meta.downloadUrl,
                                  `passport_${props.passport.id}_${Date.now()}`,
                                )
                              }
                            },
                            onError: (error: Error) => {
                              toast.negative(error)
                            },
                          },
                        ]
                      : undefined
                  }
                  onChange={([passportFile]) => {
                    if (passportFile) {
                      setShowUploadErrorNotice(false)

                      if (
                        !ALLOWED_PASSPORT_FILE_TYPES.find(fileExtension =>
                          passportFile.file.name.toLowerCase().endsWith(fileExtension),
                        )
                      ) {
                        passportFile.errorMessage = `Incorrect file format. Only the following file types may be uploaded: ${ALLOWED_PASSPORT_FILE_TYPES.join(
                          ', ',
                        )}`

                        return false
                      }

                      uploadPassport(
                        transformFileInfoToUploadFileData(passportFile, {
                          sectionReference: `${props.passport?.id}`,
                          type: DocumentTags.PASSPORT as unknown as DocumentType,
                        }),
                        {
                          onSuccess: async response => {
                            await queryClient.setQueryData(
                              ['passport', response.data.id],
                              response.data,
                            )
                            props.setPassportId(response.data.id)
                          },
                          onError: err => {
                            setShowUploadErrorNotice(true)
                            passportFile.errorMessage = `Error while trying to upload the file. Please try again.`
                            setFieldValues({ passportFiles: [passportFile] })
                            toast.negative(err)
                          },
                        },
                      )
                    }
                  }}
                />
                {(!props.passport?.id || isUploadingPassport) && (
                  <Text variant="bodyS" contrast="mid">
                    Supported file formats: JPG, JPEG, PDF, PNG, max number of files:{' '}
                    {UPLOAD_LIMIT}{' '}
                  </Text>
                )}
              </Flex>
            </Flex>
          </>
        ) : null}

        {!isExtractingData || !isAiProcessingEnabled ? (
          <>
            <Divider />
            <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap pt={4} pb={4}>
              <Flex basis="100%">
                <Heading variant="titleS" level={3}>
                  ✈️ Step 2: Provide additional information
                </Heading>
              </Flex>
              <Flex basis="100%">
                <Heading variant="titleS" level={3}>
                  <span aria-hidden>🏢</span> Residential Address
                </Heading>
              </Flex>
              <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                <Field
                  as={Combobox}
                  label="Country"
                  name="residentialAddressCountry"
                  size="md"
                  placeholder="Select"
                  disabled={props.disabled || props.isRequestingPassportApi}
                  required={!props.disabled ? 'Country is required' : false}
                  onChange={value => {
                    if (isMailingAddressResidentialAddress) {
                      setFieldValues({
                        ...copyResidentialAddress({
                          residentialAddressCountry,
                          residentialAddressLine1,
                          residentialAddressLine2,
                          residentialAddressCity,
                          residentialAddressProvince,
                          residentialAddressPostalCode,
                        }),
                        mailingAddressCountry: value as string,
                      })
                    }
                  }}
                >
                  {Object.entries(COUNTRY_NAMES).map(([countryCode, countryName]) => (
                    <Combobox.Option key={countryCode} label={countryName} value={countryCode} />
                  ))}
                </Field>
              </Flex.Item>
              <Flex pb={6} gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
                <Flex.Item basis={{ xs: '100%' }}>
                  <Field
                    as={TextInput as ComponentType<TextInputProps>}
                    label="Address line 1"
                    name="residentialAddressLine1"
                    disabled={props.disabled || props.isRequestingPassportApi}
                    required={!props.disabled ? 'Address is required' : false}
                    helpText="E.g. Street address"
                    onChange={value => {
                      if (isMailingAddressResidentialAddress) {
                        setFieldValues({
                          ...copyResidentialAddress({
                            residentialAddressCountry,
                            residentialAddressLine1,
                            residentialAddressLine2,
                            residentialAddressCity,
                            residentialAddressProvince,
                            residentialAddressPostalCode,
                          }),
                          mailingAddressLine1: value as string,
                        })
                      }
                    }}
                  />
                </Flex.Item>
                <Flex.Item basis={{ xs: '100%' }}>
                  <Field
                    as={TextInput as ComponentType<TextInputProps>}
                    label="Address line 2"
                    name="residentialAddressLine2"
                    disabled={props.disabled || props.isRequestingPassportApi}
                    helpText="E.g. Apartment, suite, unit"
                    onChange={value => {
                      if (isMailingAddressResidentialAddress) {
                        setFieldValues({
                          ...copyResidentialAddress({
                            residentialAddressCountry,
                            residentialAddressLine1,
                            residentialAddressLine2,
                            residentialAddressCity,
                            residentialAddressProvince,
                            residentialAddressPostalCode,
                          }),
                          mailingAddressLine2: value as string,
                        })
                      }
                    }}
                  />
                </Flex.Item>
                <Flex basis="100%" gap={4}>
                  <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                    <Field
                      as={TextInput as ComponentType<TextInputProps>}
                      label="Province/state/region"
                      name="residentialAddressProvince"
                      disabled={props.disabled || props.isRequestingPassportApi}
                      required={!props.disabled ? 'Province/state/region is required' : false}
                      onChange={value => {
                        if (isMailingAddressResidentialAddress) {
                          setFieldValues({
                            ...copyResidentialAddress({
                              residentialAddressCountry,
                              residentialAddressLine1,
                              residentialAddressLine2,
                              residentialAddressCity,
                              residentialAddressProvince,
                              residentialAddressPostalCode,
                            }),
                            mailingAddressProvince: value as string,
                          })
                        }
                      }}
                    />
                  </Flex.Item>
                  <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                    <Field
                      as={TextInput as ComponentType<TextInputProps>}
                      label="City/town"
                      name="residentialAddressCity"
                      disabled={props.disabled || props.isRequestingPassportApi}
                      required={!props.disabled ? 'City/town is required' : false}
                      onChange={value => {
                        if (isMailingAddressResidentialAddress) {
                          setFieldValues({
                            ...copyResidentialAddress({
                              residentialAddressCountry,
                              residentialAddressLine1,
                              residentialAddressLine2,
                              residentialAddressCity,
                              residentialAddressProvince,
                              residentialAddressPostalCode,
                            }),
                            mailingAddressCity: value as string,
                          })
                        }
                      }}
                    />
                  </Flex.Item>
                </Flex>
                <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                  <Field
                    as={TextInput as ComponentType<TextInputProps>}
                    label="Postal/zip code"
                    name="residentialAddressPostalCode"
                    disabled={props.disabled || props.isRequestingPassportApi}
                    onChange={value => {
                      if (isMailingAddressResidentialAddress) {
                        setFieldValues({
                          ...copyResidentialAddress({
                            residentialAddressCountry,
                            residentialAddressLine1,
                            residentialAddressLine2,
                            residentialAddressCity,
                            residentialAddressProvince,
                            residentialAddressPostalCode,
                          }),
                          mailingAddressPostalCode: value as string,
                        })
                      }
                    }}
                  />
                </Flex.Item>
              </Flex>

              <Flex direction={'column'}>
                <Flex basis={'100%'}>
                  <Heading variant="titleS" level={3}>
                    <span aria-hidden>✉️</span> Full Mailing Address
                  </Heading>
                </Flex>

                <Flex basis={'100%'}>
                  <Field
                    as={Checkbox as ComponentType<CheckboxProps>}
                    name="isMailingAddressResidentialAddress"
                    label="Mailing address is the same as residential address"
                    disabled={props.disabled || props.isRequestingPassportApi}
                    onChange={value => {
                      if (!value) {
                        setFieldValues({
                          mailingAddressCountry: '',
                          mailingAddressLine1: '',
                          mailingAddressLine2: '',
                          mailingAddressCity: '',
                          mailingAddressProvince: '',
                          mailingAddressPostalCode: '',
                        })
                      } else {
                        setFieldValues(
                          copyResidentialAddress({
                            residentialAddressCountry,
                            residentialAddressLine1,
                            residentialAddressLine2,
                            residentialAddressCity,
                            residentialAddressProvince,
                            residentialAddressPostalCode,
                          }),
                        )
                      }
                    }}
                  />
                </Flex>

                {!isMailingAddressResidentialAddress ? (
                  <>
                    <Flex basis={'100%'} pb={4}>
                      <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                        <Field
                          as={Combobox}
                          label="Country"
                          name="mailingAddressCountry"
                          size="md"
                          placeholder="Select"
                          disabled={props.disabled || props.isRequestingPassportApi}
                          required={!props.disabled ? 'Country is required' : false}
                        >
                          {Object.entries(COUNTRY_NAMES).map(([countryCode, countryName]) => (
                            <Combobox.Option
                              key={countryCode}
                              label={countryName}
                              value={countryCode}
                            />
                          ))}
                        </Field>
                      </Flex.Item>
                    </Flex>

                    <Flex gap={4} direction={{ xs: 'column', sm: 'row' }} wrap>
                      <Flex.Item basis={{ xs: '100%' }}>
                        <Field
                          as={TextInput as ComponentType<TextInputProps>}
                          label="Address line 1"
                          name="mailingAddressLine1"
                          disabled={props.disabled || props.isRequestingPassportApi}
                          required={!props.disabled ? 'Address is required' : false}
                          helpText="E.g. Street address"
                        />
                      </Flex.Item>
                      <Flex.Item basis={{ xs: '100%' }}>
                        <Field
                          as={TextInput as ComponentType<TextInputProps>}
                          label="Address line 2"
                          name="mailingAddressLine2"
                          disabled={props.disabled || props.isRequestingPassportApi}
                          helpText="E.g. Apartment, suite, unit"
                        />
                      </Flex.Item>
                      <Flex basis="100%" gap={4}>
                        <Flex.Item basis={{ xs: 'calc(50% - 8px)' }}>
                          <Field
                            as={TextInput as ComponentType<TextInputProps>}
                            label="Province/state/region"
                            name="mailingAddressProvince"
                            disabled={props.disabled || props.isRequestingPassportApi}
                            required={!props.disabled ? 'Province/state/region is required' : false}
                          />
                        </Flex.Item>
                        <Flex.Item basis={{ xs: 'calc(50% - 8px)' }}>
                          <Field
                            as={TextInput as ComponentType<TextInputProps>}
                            label="City/town"
                            name="mailingAddressCity"
                            disabled={props.disabled || props.isRequestingPassportApi}
                            required={!props.disabled ? 'City/town is required' : false}
                          />
                        </Flex.Item>
                      </Flex>
                      <Flex.Item basis={{ xs: '100%', sm: 'calc(50% - 8px)' }}>
                        <Field
                          as={TextInput as ComponentType<TextInputProps>}
                          label="Postal/zip code"
                          name="mailingAddressPostalCode"
                          disabled={props.disabled || props.isRequestingPassportApi}
                        />
                      </Flex.Item>
                    </Flex>
                  </>
                ) : null}
              </Flex>
            </Flex>
            <Divider />

            <StatusSection
              Field={Field}
              application={props.application}
              disabled={props.disabled}
              isRequestingPassportApi={props.isRequestingPassportApi}
              destinationCountryName={props.destinationCountryName}
              immigrationStatus={immigrationStatus}
              immigrationFiles={immigrationFiles}
              setFieldValues={setFieldValues}
            />
          </>
        ) : null}
      </>
    )
  }

  return (
    <>
      {renderPassportUploadSection()}
      {renderFormFields()}

      {/*
        This scenario occurs only in Draft mode when no personal data has been saved.
        Once the personal data is saved, the file cannot be removed by clicking the "X"
        but can only be replaced using the "Replace" button.
      */}
      {openRemovePassportDialog && (
        <RemovePassportDialog
          open={openRemovePassportDialog}
          onOpenChange={setOpenRemovePassportDialog}
          onConfirm={() => {
            setFieldValues({
              passportFiles: [],
              passportNumber: '',
              passportExpiryDate: '',
              familyName: '',
              middleName: '',
              givenName: '',
              nationality: '',
              dateOfBirth: '',
              gender: '',
            })
            setOpenRemovePassportDialog(false)
            props.setPassportId('')
          }}
          passportId={props.passport?.id ?? ''}
          applicationId={props.application.id}
        />
      )}
    </>
  )
}
